/* PunchOut Session Inspector — Infosys Admin only.

   Route: /infoshop/admin/punchout-sessions

   The aggregator PunchOut "double jump" generates a chain of cXML
   payloads across three parties:

       Coupa/Ariba  ──POSR──▶  UniBuy™  ──PSR──▶  Sub-Supplier
                                  ▲                    │
              Transfer-to-Origin  │                    │ PunchOutOrderMessage
              PunchOutOrderMessage│                    ▼
                               UniBuy™ ◀──merge cart──┘

   When a real partner (e.g. Coupa, Grainger) rejects a PO, production
   support needs to inspect the exact wire-level payloads that flew
   between the three actors. This screen surfaces:

     • A filterable list of every session (in/outbound, status, partner)
     • For each session: cXML payloads, returned items, dynamic SKUs
       minted, transfer-to-origin POOM bodies (one per currency/tax
       bucket), and the AD identity that authorized the jump.
*/
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCw, Loader2, Network, ShieldCheck, ClipboardCopy,
  FileCode2, Box, ArrowRightLeft, ChevronRight, Filter,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useAuth } from "../../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/punchout`;

const DIRECTION_LABEL = {
  origin_inbound: "Origin → UniBuy™",
  sub_supplier_outbound: "UniBuy™ → Sub-Supplier",
};
const DIRECTION_COLOR = {
  origin_inbound: "bg-violet-100 text-violet-800 border-violet-200",
  sub_supplier_outbound: "bg-sky-100 text-sky-800 border-sky-200",
};
const STATUS_COLOR = {
  launched: "bg-slate-100 text-slate-700 border-slate-200",
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  returned: "bg-amber-100 text-amber-800 border-amber-200",
  transferred: "bg-blue-100 text-blue-800 border-blue-200",
};

function copy(text, label = "Copied") {
  navigator.clipboard?.writeText(text).then(
    () => toast.success(label),
    () => toast.error("Copy failed"),
  );
}

function fmtDt(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

function Money({ value, currency }) {
  if (value == null) return <span>—</span>;
  return (
    <span className="font-mono text-xs">
      {currency || ""} {Number(value).toFixed(2)}
    </span>
  );
}

function CxmlBlock({ label, cxml, testid }) {
  if (!cxml) return null;
  return (
    <div className="mt-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
          {label} <span className="text-slate-400 normal-case font-normal">· {cxml.length.toLocaleString()} bytes</span>
        </div>
        <button
          onClick={() => copy(cxml, `${label} copied`)}
          className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-[#0072CE]"
          data-testid={`${testid}-copy`}
        >
          <ClipboardCopy size={12} /> Copy
        </button>
      </div>
      <pre className="text-[11px] leading-snug bg-slate-900 text-slate-100 p-3 rounded-md overflow-auto max-h-72 font-mono">
        {cxml}
      </pre>
    </div>
  );
}

function SessionRow({ s, selected, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`session-row-${s.session_id}`}
      className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition ${
        selected ? "bg-[#0072CE]/5 border-l-4 border-l-[#0072CE]" : "border-l-4 border-l-transparent"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${DIRECTION_COLOR[s.direction] || ""}`}>
              {DIRECTION_LABEL[s.direction] || s.direction}
            </span>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${STATUS_COLOR[s.status] || ""}`}>
              {s.status}
            </span>
            {s.used_mock_fallback && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-orange-100 text-orange-800 border-orange-200">
                Mock fallback
              </span>
            )}
            {s.poom_split_count > 1 && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-rose-100 text-rose-800 border-rose-200">
                Split×{s.poom_split_count}
              </span>
            )}
          </div>
          <div className="mt-1 font-mono text-xs text-slate-900 truncate">
            {s.session_id}
          </div>
          <div className="mt-0.5 text-xs text-slate-600 truncate">
            {s.direction === "sub_supplier_outbound"
              ? `${s.sub_supplier_name || s.sub_supplier_code || "—"} · ${s.company_name}`
              : `${s.origin_label || s.from_identity || "Origin"} → ${s.company_name}`}
          </div>
        </div>
        <ChevronRight size={16} className="text-slate-400 shrink-0 mt-1" />
      </div>
      <div className="mt-1 text-[10px] text-slate-400">{fmtDt(s.created_at)}</div>
    </button>
  );
}

function SessionDetail({ session, dynamicSkus }) {
  if (!session) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-slate-400">
        Select a session to inspect its cXML payloads, items, and partner exchange.
      </div>
    );
  }
  const isOutbound = session.direction === "sub_supplier_outbound";
  const isInbound = session.direction === "origin_inbound";
  return (
    <div className="p-5 space-y-5" data-testid="session-detail">
      {/* Header card */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
              {DIRECTION_LABEL[session.direction] || session.direction}
            </div>
            <h2 className="font-display font-bold text-slate-900 text-lg truncate">
              {session.session_id}
            </h2>
            <div className="text-xs text-slate-600 mt-0.5">
              {isOutbound
                ? `${session.sub_supplier_name || "Sub-Supplier"} · ${session.punchout_url || "—"}`
                : `${session.origin_label || "Origin"} · ${session.browser_form_post_url || "—"}`}
            </div>
          </div>
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${STATUS_COLOR[session.status] || ""}`}>
            {session.status}
          </span>
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div>
            <div className="text-slate-400 text-[10px] uppercase tracking-wider">Buyer</div>
            <div className="font-mono text-slate-900 truncate">{session.network_id}</div>
            <div className="text-slate-500 truncate">{session.company_name}</div>
          </div>
          <div>
            <div className="text-slate-400 text-[10px] uppercase tracking-wider">Country / Currency</div>
            <div className="font-mono text-slate-900">{session.country_code} · {session.currency}</div>
          </div>
          <div>
            <div className="text-slate-400 text-[10px] uppercase tracking-wider">Buyer Cookie</div>
            <div className="font-mono text-slate-900 truncate" title={session.buyer_cookie}>{session.buyer_cookie}</div>
          </div>
          <div>
            <div className="text-slate-400 text-[10px] uppercase tracking-wider">Timing</div>
            <div className="text-slate-900">{fmtDt(session.created_at)}</div>
            {session.transferred_at && (
              <div className="text-slate-500 text-[11px]">↻ transferred {fmtDt(session.transferred_at)}</div>
            )}
            {session.returned_at && (
              <div className="text-slate-500 text-[11px]">↻ returned {fmtDt(session.returned_at)}</div>
            )}
          </div>
        </div>
        {session.ad_email && (
          <div className="mt-3 flex items-center gap-2 text-xs bg-violet-50 border border-violet-200 px-3 py-2 rounded">
            <ShieldCheck size={14} className="text-violet-700" />
            <span className="text-violet-900">
              Microsoft AD authorized: <strong>{session.ad_name || session.ad_email}</strong>
              <span className="text-violet-600 ml-1">({session.ad_email})</span>
            </span>
          </div>
        )}
        {session.real_failure_reason && (
          <div className="mt-2 text-xs bg-rose-50 border border-rose-200 text-rose-900 px-3 py-2 rounded">
            <strong>Partner failure:</strong> {session.real_failure_reason}
          </div>
        )}
      </div>

      {/* OUTBOUND PunchOut: outbound + response cXML */}
      {isOutbound && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-2">
            <FileCode2 size={16} className="text-[#0072CE]" />
            <h3 className="font-display font-semibold text-slate-900">PunchOutSetup exchange</h3>
          </div>
          <CxmlBlock
            label="Outbound PunchOutSetupRequest (UniBuy™ → partner)"
            cxml={session.outbound_cxml}
            testid="cxml-outbound"
          />
          <CxmlBlock
            label="Partner PunchOutSetupResponse"
            cxml={session.real_response_cxml}
            testid="cxml-partner-response"
          />
          {session.real_start_url && (
            <div className="mt-3 text-xs bg-slate-50 border border-slate-200 px-3 py-2 rounded">
              <div className="text-slate-500 text-[10px] uppercase tracking-wider">Partner start URL</div>
              <a
                href={session.real_start_url}
                target="_blank"
                rel="noreferrer noopener"
                className="font-mono text-[#0072CE] break-all hover:underline"
              >
                {session.real_start_url}
              </a>
            </div>
          )}
        </div>
      )}

      {/* OUTBOUND: returned POOM */}
      {isOutbound && (session.returned_items?.length > 0 || session.returned_cxml) && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-2">
            <ArrowRightLeft size={16} className="text-[#0072CE]" />
            <h3 className="font-display font-semibold text-slate-900">
              PunchOutOrderMessage returned by partner
            </h3>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {session.returned_items?.length || 0} item(s) merged into the buyer&apos;s UniBuy™ cart.
          </p>
          {(session.returned_items || []).length > 0 && (
            <div className="mt-3 border border-slate-100 rounded-md divide-y divide-slate-100">
              {session.returned_items.map((it, i) => {
                const sku = (dynamicSkus || []).find((d) => d.infoshop_pn === it.infoshop_pn);
                return (
                  <div key={i} className="px-3 py-2 text-xs grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-3 font-mono text-slate-500 truncate" title={it.external_sku}>
                      ext: {it.external_sku || "—"}
                    </div>
                    <div className="col-span-3 font-mono text-slate-900 truncate">{it.infoshop_pn}</div>
                    <div className="col-span-4 text-slate-700 truncate">{sku?.description || "(dynamic SKU)"}</div>
                    <div className="col-span-1 text-slate-600 text-right">×{it.quantity}</div>
                    <div className="col-span-1 text-right">
                      <Money value={sku?.selling_price} currency={sku?.currency} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <CxmlBlock
            label="Returned PunchOutOrderMessage (partner → UniBuy™)"
            cxml={session.returned_cxml}
            testid="cxml-returned-poom"
          />
        </div>
      )}

      {/* INBOUND POSR cXML */}
      {isInbound && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-2">
            <FileCode2 size={16} className="text-[#0072CE]" />
            <h3 className="font-display font-semibold text-slate-900">PunchOutSetup exchange</h3>
          </div>
          <CxmlBlock
            label="Inbound PunchOutSetupRequest (origin → UniBuy™)"
            cxml={session.inbound_posr_cxml}
            testid="cxml-inbound-posr"
          />
          <CxmlBlock
            label="UniBuy™ PunchOutSetupResponse"
            cxml={session.inbound_posr_response_cxml}
            testid="cxml-posr-response"
          />
          {session.start_page_url && (
            <div className="mt-3 text-xs bg-slate-50 border border-slate-200 px-3 py-2 rounded">
              <div className="text-slate-500 text-[10px] uppercase tracking-wider">UniBuy™ StartPage URL</div>
              <a
                href={session.start_page_url}
                target="_blank"
                rel="noreferrer noopener"
                className="font-mono text-[#0072CE] break-all hover:underline"
              >
                {session.start_page_url}
              </a>
            </div>
          )}
        </div>
      )}

      {/* INBOUND: transfer-to-origin POOM split */}
      {isInbound && (session.transfer_poom_messages?.length || 0) > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-2">
            <ArrowRightLeft size={16} className="text-[#0072CE]" />
            <h3 className="font-display font-semibold text-slate-900">
              Transfer-to-Origin · {session.poom_split_count} POOM{session.poom_split_count > 1 ? "s" : ""}
            </h3>
          </div>
          {(session.poom_split_reasons || []).length > 0 && (
            <div className="mt-2 text-xs bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 rounded">
              <strong>Split reasons:</strong> {session.poom_split_reasons.join("; ")}
            </div>
          )}
          {(session.transfer_poom_messages || []).map((m, i) => (
            <div key={i} className="mt-3 border border-slate-100 rounded-md p-3">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-xs">
                  <span className="font-bold text-slate-900">POOM #{i + 1}</span>
                  <span className="text-slate-500 ml-2">
                    {m.tax_class === "sub_supplier_tax_incl"
                      ? "Sub-supplier · tax-inclusive"
                      : m.tax_class === "sub_supplier_tax_excl"
                      ? "Sub-supplier · tax not included"
                      : m.tax_class === "sub_supplier"
                      ? "Sub-supplier-priced"
                      : "Hosted (tax-bearing)"} ·{" "}
                    {m.item_count} item(s) · <Money value={m.total} currency={m.currency} />
                  </span>
                </div>
              </div>
              <CxmlBlock
                label={`POOM cXML (→ ${session.origin_label || "Origin"})`}
                cxml={m.cxml}
                testid={`cxml-transfer-poom-${i}`}
              />
            </div>
          ))}
        </div>
      )}

      {/* Cart snapshot (transferred sessions) */}
      {(session.cart_snapshot || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-2">
            <Box size={16} className="text-[#0072CE]" />
            <h3 className="font-display font-semibold text-slate-900">Cart snapshot at transfer</h3>
          </div>
          <div className="mt-2 border border-slate-100 rounded-md divide-y divide-slate-100">
            {session.cart_snapshot.map((it, i) => (
              <div key={i} className="px-3 py-2 text-xs grid grid-cols-12 gap-2 items-center">
                <div className="col-span-3 font-mono text-slate-900 truncate">{it.infoshop_pn}</div>
                <div className="col-span-5 text-slate-700 truncate">{it.description}</div>
                <div className="col-span-1 text-slate-600 text-right">×{it.quantity}</div>
                <div className="col-span-2 text-right">
                  <Money value={it.unit_price * it.quantity} currency={it.currency} />
                </div>
                <div className="col-span-1 text-right">
                  {it.source_type === "sub_supplier_punchout" ? (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-100 text-sky-800">
                      Sub
                    </span>
                  ) : (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                      Hosted
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PunchOutSessionsAdminPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [sessions, setSessions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterDir, setFilterDir] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [search, setSearch] = useState("");

  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [skus, setSkus] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (filterDir) params.set("direction", filterDir);
      if (filterStatus) params.set("status", filterStatus);
      const { data } = await axios.get(`${API}/sessions?${params.toString()}`);
      setSessions(data.sessions || []);
      setSummary(data.summary || null);
    } catch {
      toast.error("Failed to load sessions");
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (sessionId) => {
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/session/${sessionId}`);
      setSelected(data);
      // For outbound (sub_supplier_outbound) sessions, also fetch dynamic SKUs.
      if (data?.direction === "sub_supplier_outbound") {
        try {
          const r = await axios.get(`${API}/sessions/${sessionId}/dynamic-skus`);
          setSkus(r.data?.dynamic_skus || []);
        } catch {
          setSkus([]);
        }
      } else {
        setSkus([]);
      }
    } catch {
      toast.error("Failed to load session detail");
      setSelected(null);
    } finally {
      setDetailLoading(false);
    }
  };

  // Re-fetch the list whenever filters change.
  useEffect(() => { load(); }, [filterDir, filterStatus]);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
  }, [selectedId]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter((s) =>
      [
        s.session_id, s.network_id, s.company_name, s.sub_supplier_name,
        s.sub_supplier_code, s.origin_label, s.from_identity, s.ad_email,
      ].some((v) => (v || "").toString().toLowerCase().includes(q)),
    );
  }, [sessions, search]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="text-center">
          <h1 className="font-display text-2xl font-bold text-slate-900">Admin only</h1>
          <p className="text-sm text-slate-600 mt-2">
            PunchOut session inspection is restricted to Infosys BPM administrators.
          </p>
          <Link
            to="/infoshop"
            data-testid="po-sessions-back-unauth"
            className="text-[#0072CE] font-semibold text-sm hover:underline mt-3 inline-block"
          >
            Back to UniBuy™
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center gap-4">
          <Link
            to="/infoshop"
            data-testid="po-sessions-back-link"
            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft size={14} /> Back to UniBuy™
          </Link>
          <div className="flex items-center gap-2 ml-2">
            <Network size={18} className="text-[#0072CE]" />
            <div className="leading-tight">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                Admin Workbench
              </div>
              <h1 className="font-display font-bold text-slate-900">PunchOut Session Inspector</h1>
            </div>
          </div>
          <Button
            onClick={load}
            variant="outline"
            data-testid="po-sessions-refresh"
            className="ml-auto"
          >
            <RefreshCw size={14} className="mr-1.5" /> Refresh
          </Button>
        </div>
        {summary && (
          <div className="max-w-7xl mx-auto px-6 pb-3 flex items-center gap-3 flex-wrap text-[11px] text-slate-600">
            <span className="font-bold text-slate-900">{summary.total}</span> total sessions
            <span className="text-slate-300">·</span>
            <span><strong>{summary.by_direction?.origin_inbound || 0}</strong> origin→UniBuy™</span>
            <span className="text-slate-300">·</span>
            <span><strong>{summary.by_direction?.sub_supplier_outbound || 0}</strong> UniBuy™→sub-supplier</span>
            <span className="text-slate-300">·</span>
            <span><strong>{summary.by_status?.returned || 0}</strong> returned</span>
            <span className="text-slate-300">·</span>
            <span><strong>{summary.by_status?.transferred || 0}</strong> transferred</span>
            <span className="text-slate-300">·</span>
            <span><strong>{summary.returned_dynamic_skus || 0}</strong> dynamic SKUs minted</span>
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-6">
        {/* Left: session list */}
        <aside className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 space-y-2">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">
              <Filter size={12} /> Filters
            </div>
            <Input
              placeholder="Search id, buyer, partner, email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="po-sessions-search"
              className="text-xs"
            />
            <div className="grid grid-cols-2 gap-2">
              <select
                value={filterDir}
                onChange={(e) => setFilterDir(e.target.value)}
                data-testid="po-sessions-filter-direction"
                className="text-xs border border-slate-200 rounded px-2 py-1.5 bg-white"
              >
                <option value="">All directions</option>
                <option value="origin_inbound">Origin → UniBuy™</option>
                <option value="sub_supplier_outbound">UniBuy™ → Sub-Supplier</option>
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                data-testid="po-sessions-filter-status"
                className="text-xs border border-slate-200 rounded px-2 py-1.5 bg-white"
              >
                <option value="">All statuses</option>
                <option value="launched">launched</option>
                <option value="active">active</option>
                <option value="returned">returned</option>
                <option value="transferred">transferred</option>
              </select>
            </div>
          </div>
          <div className="max-h-[70vh] overflow-y-auto" data-testid="po-sessions-list">
            {loading && (
              <div className="p-8 text-center">
                <Loader2 size={18} className="animate-spin mx-auto text-slate-400" />
              </div>
            )}
            {!loading && visible.length === 0 && (
              <div className="p-8 text-center text-xs text-slate-500">
                No sessions match the current filters.
              </div>
            )}
            {!loading && visible.map((s) => (
              <SessionRow
                key={s.session_id}
                s={s}
                selected={s.session_id === selectedId}
                onClick={() => setSelectedId(s.session_id)}
              />
            ))}
          </div>
        </aside>

        {/* Right: session detail */}
        <section className="bg-slate-100 border border-slate-200 rounded-xl min-h-[70vh]">
          {detailLoading && (
            <div className="h-full flex items-center justify-center">
              <Loader2 size={20} className="animate-spin text-slate-400" />
            </div>
          )}
          {!detailLoading && <SessionDetail session={selected} dynamicSkus={skus} />}
        </section>
      </main>
    </div>
  );
}
